/**
 * Render agent self-measurement (Command Plane Phase 1). Moved out of
 * EdgeFleet.tsx in release 3.1 so the device drawer can show it too (the
 * fleet table's columns are now the ones an operator finds a device by);
 * EdgeFleet.tsx re-exports these names.
 *
 * THE RULE THESE ENCODE: null means NOT REPORTED and must never render as 0.
 * Until 2026-08-11 no agent measured itself at all, and every agent class
 * except the scanner still reports nothing - so most rows legitimately have no
 * reading. A 0% in those rows would tell an operator that an un-instrumented
 * agent is idle, which is precisely the false-confidence the command-centre
 * design doc warns about ("the honest console label is 'last reported', never
 * 'protected'").
 *
 * A dash is deliberately not a zero and not a blank: blank reads as a
 * rendering bug, zero reads as a measurement. (The glyph is the one the
 * existing tests pin; changing it is a separate, visible decision.)
 */
export const NOT_REPORTED = String.fromCharCode(0x2014);

/**
 * Prefer the instantaneous rate when the agent could honestly observe one;
 * otherwise fall back to cumulative CPU seconds, which is meaningful even for
 * the one-shot scanner. Only if BOTH are absent do we say "not reported".
 */
export function formatCpu(p: { cpuPercent: number | null; cpuSecondsTotal: number | null }): string {
  if (p.cpuPercent != null) return `${p.cpuPercent.toFixed(1)}%`;
  if (p.cpuSecondsTotal != null) return `${p.cpuSecondsTotal.toFixed(1)}s`;
  return NOT_REPORTED;
}

/** Explains which of the two numbers the cell is showing, and why. */
export function cpuTitle(p: {
  cpuPercent: number | null;
  cpuSecondsTotal: number | null;
  measuredAt: string | null;
}): string {
  if (p.cpuPercent == null && p.cpuSecondsTotal == null) {
    return 'No measurement reported by this agent.';
  }
  const when = p.measuredAt ? ` (sampled ${new Date(p.measuredAt).toLocaleString()})` : '';
  return p.cpuPercent != null
    ? `Instantaneous CPU, may exceed 100% on multicore${when}`
    : `Cumulative CPU seconds - this agent runs one-shot, so no rate could be observed${when}`;
}

export function formatRss(rssMb: number | null): string {
  if (rssMb == null) return NOT_REPORTED;
  return rssMb >= 1024 ? `${(rssMb / 1024).toFixed(1)} GiB` : `${rssMb} MiB`;
}
